/**
 * Firebase Configuration
 * Initializes Firebase app and auth for authentication
 * 
 * ⚠️ IMPORTANT: Firebase is used ONLY for authentication.
 * All business logic resides in backend services.
 */

import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth } from 'firebase/auth';
import { getApiConfig } from '../api/config';

const apiConfig = getApiConfig();

// Firebase configuration
// These values should be set in environment variables
// Type for window with environment variables
interface WindowWithEnv extends Window {
  __ENV__?: {
    VITE_FIREBASE_API_KEY?: string;
    VITE_FIREBASE_AUTH_DOMAIN?: string;
  };
}

const firebaseConfig = {
  apiKey: typeof window !== 'undefined' 
    ? (window as WindowWithEnv).__ENV__?.VITE_FIREBASE_API_KEY || import.meta.env?.VITE_FIREBASE_API_KEY
    : process.env.VITE_FIREBASE_API_KEY,
  authDomain: typeof window !== 'undefined'
    ? (window as WindowWithEnv).__ENV__?.VITE_FIREBASE_AUTH_DOMAIN || import.meta.env?.VITE_FIREBASE_AUTH_DOMAIN
    : process.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: apiConfig.gcpProjectId, // Uses dev project (flowrithm-dev) by default
};

// Initialize Firebase
let firebaseApp: FirebaseApp | null = null;
let firebaseAuth: Auth | null = null;

try {
  firebaseApp = initializeApp(firebaseConfig);
  firebaseAuth = getAuth(firebaseApp);
} catch (error) {
  // Firebase will be initialized when credentials are available
  // Error will be handled when credentials are provided
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.warn('Firebase initialization skipped - credentials not configured');
  }
}

export { firebaseApp, firebaseAuth };

/**
 * Get Firebase Auth instance
 * Returns null if Firebase is not initialized
 */
export const getFirebaseAuth = (): Auth | null => {
  return firebaseAuth;
};

